import { Pencil } from "@phosphor-icons/react"
import {
  IconButton,
  type IconButtonProps,
} from "@tokenterminal/core/ui/button/IconButton"
import { ColorSelector } from "modules/chart-builder/components/SeriesTab/SerieSelector/ColorSelector"
import {
  type Dispatch,
  type SetStateAction,
  type HTMLAttributes,
  useState,
  useRef,
} from "react"
import { Popover } from "react-aria-components"
import { twMerge } from "tailwind-merge"
import { getContrastingColor, useKeyColors } from "../hooks/useKeyColors"
import {
  type BackgroundPreset,
  backgroundPresets,
  getBackgroundStyles,
} from "../themes"
import { type ChartImageState } from "../types"

const BackgroundColorSwatch = ({
  className,
  isSelected,
  ...props
}: HTMLAttributes<HTMLButtonElement> & {
  isSelected: boolean
}) => {
  return (
    <button
      className={twMerge(
        `w-6 h-6 rounded flex-shrink-0 border border-solid border-neutral-500/50 hover:border-border-default ${isSelected ? "outline outline-2 outline-primary hover:outline-primary" : ""}`,
        className
      )}
      aria-label={`Select background`}
      {...props}
    />
  )
}

const CustomColorSwatch = ({
  className,
  onChange,
  value,
  isSelected,
  ...props
}: Omit<IconButtonProps, "icon" | "label"> & {
  onChange: (color: string) => void
  value: string
  isSelected: boolean
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <IconButton
        ref={triggerRef}
        onPress={() => {
          setIsOpen(true)
          onChange(value)
        }}
        className={twMerge(
          `!w-6 !h-6 !rounded flex items-center justify-center flex-shrink-0`,
          isSelected ? "outline outline-2 outline-primary" : "",
          className
        )}
        icon={Pencil}
        label="Add custom background"
        style={{ backgroundColor: value, color: getContrastingColor(value) }}
      />
      <Popover
        placement="bottom end"
        className="tt-popover rounded-[theme(spacing[2.5])] flex flex-col overflow-hidden p-0"
        triggerRef={triggerRef}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
      >
        <div className="p-2">
          <ColorSelector color={value} onChange={onChange} />
        </div>
      </Popover>
    </>
  )
}

type BackgroundColorSelectorProps = {
  state: ChartImageState
  setState: Dispatch<SetStateAction<ChartImageState>>
}

const BackgroundColorSelector = ({
  state,
  setState,
}: BackgroundColorSelectorProps) => {
  const { data: { uniqueColors: accentColors = [] } = {} } = useKeyColors(
    state.image.type === "project"
      ? `https://static1.tokenterminal.com/${state.image.projectLogo}`
      : state.image.customUrl
  )

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <label className="tt-field-label text-fg-secondary">Backgrounds</label>
        <div className="flex gap-1 flex-wrap">
          {Object.keys(backgroundPresets).map((bg, index) => {
            const { background } = getBackgroundStyles({
              type: "preset",
              presetKey: bg as BackgroundPreset,
            })

            return (
              <BackgroundColorSwatch
                key={index}
                className={background.className}
                isSelected={
                  state.background.type === "preset" &&
                  state.background.presetKey === bg
                }
                onClick={() => {
                  setState((prev) => ({
                    ...prev,
                    background: {
                      ...prev.background,
                      type: "preset",
                      presetKey: bg as BackgroundPreset,
                    },
                  }))
                }}
                style={background.style}
              />
            )
          })}
          <CustomColorSwatch
            onChange={(color) => {
              setState((prev) => ({
                ...prev,
                background: {
                  ...prev.background,
                  type: "custom",
                  customColor: color,
                },
              }))
            }}
            value={state.background.customColor ?? "#ffffff"}
            isSelected={state.background.type === "custom"}
          />
        </div>
      </div>
      {accentColors.length > 0 && (
        <div className="flex flex-col gap-1">
          <label className="tt-field-label text-fg-secondary">Suggested</label>
          <div className="flex gap-1 flex-wrap">
            {accentColors.map((color) => {
              const { background } = getBackgroundStyles({
                type: "custom",
                customColor: color,
              })

              return (
                <BackgroundColorSwatch
                  key={color}
                  className={background.className}
                  isSelected={
                    state.background.type === "custom" &&
                    state.background.customColor === color
                  }
                  onClick={() => {
                    setState((prev) => ({
                      ...prev,
                      background: {
                        ...prev.background,
                        type: "custom",
                        customColor: color,
                      },
                    }))
                  }}
                  style={background.style}
                />
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default BackgroundColorSelector
